import type { RaceQuery } from "../../../generated/graphql";
import tabWin from '../../../assets/tab_win_hdr.png'
import tabPlc from '../../../assets/tab_plc_hdr.png'
import sportsbetWin from '../../../assets/sportsbet_win_hdr.png'
import ladbrokesWin from '../../../assets/ladbrokes_win_hdr.png'
import styles from './RaceGrid.module.css'
import { Card, Grid, Box } from "@mui/material";
import { useEffect, useRef } from "react";
import clsx from "clsx";
import { Sparklines, SparklinesLine } from "react-sparklines"
import useAppState from "../../../hooks/useAppState";

interface Props {
  race: null | Omit<NonNullable<RaceQuery['race']>, '__typename'>;
}

type State = "NSW" | "QLD" | "VIC";

export default function RaceGrid ( { race }: Props) {
  const runnersRef = useRef<HTMLDivElement>(null);
  const scrollDirectionRef = useRef(1); // 1 for scrolling down, -1 for scrolling up
  const maxScrollTopRef = useRef(0);
  const isPausedRef = useRef(false);
  const { controllerState, additionalWinPriceColumn } = useAppState();

  useEffect(() => {
    const scrollRunners = () => {
      if (runnersRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = runnersRef.current;

        if (!isPausedRef.current) {
          if (scrollDirectionRef.current === 1) {
            if (scrollTop + clientHeight >= scrollHeight) {
              isPausedRef.current = true;
              maxScrollTopRef.current = scrollTop;
              setTimeout(() => {
                isPausedRef.current = false;
                scrollDirectionRef.current = -1;
              }, 5000); // Pause at the bottom for 5 seconds
            }
          } else {
            if (scrollTop <= 0) {
              isPausedRef.current = true;
              setTimeout(() => {
                isPausedRef.current = false;
                scrollDirectionRef.current = 1;
              }, 5000); // Pause at the top for 5 seconds
            }
          }

          if (!isPausedRef.current) {
            const newScrollTop = scrollTop + scrollDirectionRef.current;
            runnersRef.current.scrollTop = newScrollTop;
          }
        }
      }
    };

    const scrollInterval = setInterval(scrollRunners, 40); // Adjust the scroll speed as needed

    return () => {
      clearInterval(scrollInterval);
    };
  }, []);

  function findPrices(tabNo: number, type: "WIN" | "PLACE" | "WIN_FIXED_ODDS", additionalColumnEnable: boolean) {
    // const state = race?.meeting.track.state as "NSW" | "QLD" | "VIC" || "NSW"
    const state: State = (["NSW", "QLD", "VIC"] as const).includes(controllerState as State)
      ? (controllerState as State)
      : "NSW";
    const stateMapping = {
      "VIC": "V",
      "NSW": "N",
      "QLD": "Q"
    }
    const competitor = race?.competitors.find((a) => a.tabNo === tabNo)

    const source = additionalColumnEnable
      ? additionalWinPriceColumn
      : stateMapping[state];

    const fixedPrice =
      competitor?.prices.find((a) => a.type === type && a.source === source)
        ?.price ?? 0;

    const formatted = fixedPrice.toFixed(1);

    return formatted.endsWith('.0')
      ? formatted.slice(0, -2)
      : formatted;
  }

  function findFlucs(tabNo: number) {
    const competitor = race?.competitors.find((a) => a.tabNo === tabNo)
    if (!competitor) return []

    const flucsDeduped = Object.values(
      competitor.flucs.reduce<Record<number, { price: number, time: string }>>((acc, fluc) => {
        acc[fluc.price] = fluc;
        return acc;
      }, {})
    );

    const flucs = flucsDeduped.slice(-5)
    return flucs
  }

  function findFlucsGraph(tabNo: number) {
    const competitor = race?.competitors.find((a) => a.tabNo === tabNo)
    if (!competitor) return []
    const flucs = competitor.flucs

    const flucsDeduped = Object.values(
      flucs.reduce<Record<number, { price: number, time: string }>>((acc, fluc) => {
        acc[fluc.price] = fluc;
        return acc;
      }, {})
    );

    // if (!flucsDeduped.length) return [];

    // if (flucsDeduped.length <= 2) return flucsDeduped.slice(-5).map((a) => {return a.price})

    // const firstPrice = flucsDeduped[0].price;
    // const { lowestPrice, highestPrice, lowestPriceIndex, highestPriceIndex } = flucsDeduped.reduce(
    //   (acc, { price }, index) => {
    //     if (price < acc.lowestPrice) {
    //       return { lowestPrice: price, highestPrice: acc.highestPrice, lowestPriceIndex: index, highestPriceIndex: acc.highestPriceIndex };
    //     }
    //     if (price > acc.highestPrice) {
    //       return { lowestPrice: acc.lowestPrice, highestPrice: price, lowestPriceIndex: acc.lowestPriceIndex, highestPriceIndex: index };
    //     }
    //     return acc;
    //     },
    //   { lowestPrice: Infinity, highestPrice: -Infinity, lowestPriceIndex: -1, highestPriceIndex: -1 }
    //   );
    // const lastPrice = flucsDeduped[flucsDeduped.length - 1].price;

    // if (lowestPriceIndex > highestPriceIndex) return [firstPrice, highestPrice, lowestPrice, lastPrice];
    // else return [firstPrice, lowestPrice, highestPrice, lastPrice];
    return flucsDeduped.map((a) => {return a.price})
  }

  function findState(state: string, length: 'short' | 'long') {
    if (length === 'long') {
      switch (state) {
        case 'NSW':
          return 'New South Wales'
        case 'QLD':
          return 'Queensland'
        case 'VIC':
          return 'Victorian'
        default:
          return ""
      }
    } else if (length === 'short') {
      switch (state) {
        case 'NSW':
          return 'N'
        case 'QLD':
          return 'Q'
        case 'VIC':
          return 'V'
        default:
          return ""
      }
    }
  }

  if (!race) return null;

  return (
    <div style={{ 'fontSize': '2.6rem' }}>
      <Box sx={{ flexGrow: 0 }}>
        <Grid container spacing={0} columns={24} style={{ 'backgroundColor': 'lightGrey' }} id="runnerHeader">
          <Grid xs={10} sx={{ pl: 3 }}>
            <div className={styles.tableHeader}>
              <span style={{ 'marginRight': '5rem' }}>
                Track: {race.trackCondition}
              </span>
              <span>
                Weather: {race.weather}
              </span>
            </div>
          </Grid>
          <Grid xs={true}>
            <div className={styles.tableHeader}>
            <span>
              {findState(race.meeting.track.state, 'short')}OP ({findState(race.meeting.track.state, 'long')} Official Price)
            </span>
            </div>
          </Grid>
          {additionalWinPriceColumn !== 'none' && (
            <Grid xs="auto" sx={{ display: 'flex', height: '100%'}}>
              <img src={additionalWinPriceColumn === 'SB2' ? sportsbetWin : ladbrokesWin} alt={'sportsbet win'} className={styles.image} />
            </Grid>
          )}
          <Grid xs="auto" sx={{ display: 'flex', height: '100%'}}>
            <img src={tabWin} alt={'tab win'} className={styles.image} />
          </Grid>
          <Grid xs="auto" sx={{ display: 'flex', height: '100%'}}>
            <img src={tabPlc} alt={'tab plc'} className={styles.image} />
          </Grid>
        </Grid>
        <Grid container xs={0} columns={24} className={styles.raceGridLayout} id="runners" ref={runnersRef}>
          <Grid xs={10}>
            <div className={styles.competitorList}>
              {race.competitors.filter((a) => !a.scratched).map((runner) => {
                return (
                  <Card elevation={0} square key={runner.tabNo} className={styles.competitorRow}>
                    <div style={{ 'width': '5rem', 'textAlign': 'right', 'marginRight': '1rem' }}>{runner.tabNo}</div>
                    <img src={runner.silksURL} alt={'jockey silk'} width={'50'}/>
                    <div className={styles.runnerName}>
                    {runner.name} <span className={styles.runnerBarrier}>({runner.barrier})</span>
                  </div>
                  </Card>
                )
              })}
            </div>
          </Grid>
          <Grid xs={true}>
            <div className={styles.opFlucs}>
              {race.competitors.filter((a) => !a.scratched).map((runner) => {
                return (
                  <Card elevation={0} square key={runner.tabNo} className={styles.competitorRow}>
                    <Sparklines data={findFlucsGraph(runner.tabNo)} style={{'width': '200px', 'marginRight': '3rem'}}>
                      <SparklinesLine style={{ strokeWidth: 5, stroke: "#000000", fill: "none" }} />
                    </Sparklines>
                    {findFlucs(runner.tabNo).map((f) => {
                      const formatted = f.price.toFixed(1);
                      const display = formatted.endsWith('.0')
                        ? formatted.slice(0, -2)
                        : formatted;
                      return (
                        <div key={f.time} style={{'marginRight': '3rem', 'minWidth': '80px'}}>
                          {display}
                        </div>
                      )
                    })}
                  </Card>
                )
              })}
            </div>
          </Grid>
          {additionalWinPriceColumn !== 'none' && (
            <Grid xs="auto">
              <div style={{'height': '6.2rem', width: '160px'}}>
                {race.competitors.filter((a) => !a.scratched).map((runner) => {
                  return (
                    <Card elevation={0} square key={runner.tabNo} className={clsx(styles.competitorRow, styles.winOdds, styles.tabOdds)}>
                      <div>
                        {findPrices(runner.tabNo, "WIN_FIXED_ODDS", true)}
                      </div>
                    </Card>
                  )
                })}
              </div>
            </Grid>
          )}
          <Grid xs="auto">
            <div style={{'height': '6.2rem', width: '160px'}}>
              {race.competitors.filter((a) => !a.scratched).map((runner) => {
                return (
                  <Card elevation={0} square key={runner.tabNo} className={clsx(styles.competitorRow, styles.winOdds, styles.tabOdds, styles[`stateColor-${controllerState}-${race.meeting.track.state}`])}>
                    <div>
                      {findPrices(runner.tabNo, "WIN", false)}
                    </div>
                  </Card>
                )
              })}
            </div>
          </Grid>
          <Grid xs="auto">
            <div style={{'height': '6.2rem', width: '160px'}}>
              {race.competitors.filter((a) => !a.scratched).map((runner) => {
                return (
                  <Card elevation={0} square key={runner.tabNo} className={clsx(styles.competitorRow, styles.tabOdds, styles.plcOdds, styles.placeColor)}>
                    <div>
                      {findPrices(runner.tabNo, "PLACE", false)}
                    </div>
                  </Card>
                )
              })}
            </div>
          </Grid>
        </Grid>
      </Box>
    </div>
  )
}